import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/shared/ui/Card';

import { useSketches } from '../api/profiler';
import { BaselineSummary } from './BaselineSummary';

/* The profile baseline, on the Scores tab.
 *
 * **Why it belongs on this tab and not only on the Profile one.** Before this, a dataset that had
 * been profiled but had no rules bound showed the Scores tab an empty state and nothing else — so
 * the reader was told there was no score while a profile sitting one tab away had plenty to say
 * about the same table. "Profiling produced nothing you can see here" is what that reads as, and it
 * was wrong.
 *
 * **Below the rule score, never blended with it, and that ordering is the design.** Where a rule
 * score exists it is authoritative — it measures declared expectations against real counts — and
 * this demotes to context: the statistics that suggest which rules to write next. Averaging the two
 * would produce one number standing for two different kinds of claim, which is the thing
 * `profileBaseline` was shaped to avoid. Where no rule score exists, the panel above is a short
 * empty state and this becomes the substance of the tab, which is the case that prompted it.
 *
 * Renders **nothing at all** when the dataset has never been profiled. A card reading "no baseline"
 * beside an empty state reading "no score" is two ways of saying the same absence, and the Profile
 * tab already explains how to get one.
 */

export interface ProfileBaselineCardProps {
  readonly datasetId: string;
}

export function ProfileBaselineCard({
  datasetId,
}: ProfileBaselineCardProps): ReactElement | null {
  const { t } = useTranslation();
  /* The same query key the Profile tab uses, so a reader who has looked at either pays for one
     request rather than two. */
  const sketches = useSketches(datasetId);

  /* Pending and failed both render nothing here, deliberately. This is a *secondary* panel on a tab
     whose subject is the rule score, and a spinner or an error banner for it would compete with the
     thing the reader came for. The Profile tab is where a failed profile read is reported, because
     there it is the subject rather than an aside. */
  if (!sketches.isSuccess || sketches.data === null) return null;

  return (
    <Card title={t('dataset.score.baselineTitle')} hint={t('dataset.score.baselineHint')}>
      <BaselineSummary baseline={sketches.data.baseline} />
    </Card>
  );
}
